import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../firebase/useAuth';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useModal } from '../../context/ModalContext';
import { useToast } from '../../context/ToastContext';
import { fetchCramSentencesItems } from '../../firebase/cramSession';
import { fetchRecentUnlocksSentencesItems } from '../../firebase/recentUnlocks';
import { skipSentence } from '../../firebase/sentences';
import { getSound } from '../../firebase/sounds';
import levelUpSoundFile from '../../assets/sounds/correct.mp3';

// Components
import SessionLayout from '../../components/session/SessionLayout';
import InputSection from '../../components/session/InputSection';
import InteractiveCard from '../../components/session/InteractiveCard';
import Spinner from '../../components/common/Spinner';
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import ReportIssueModal from '../../components/modals/ReportIssueModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SimpleAnswerSection from '../../components/session/SimpleAnswerSection';
import { AudioPlayerProvider } from '../../context/AudioPlayerContext';
import { checkSentenceAnswer } from '../../utils/answerCheck';

const CramSentencesSessionPage = ({ mode = 'cram' }) => {
  const fetchFn = mode === 'cram' ? fetchCramSentencesItems : fetchRecentUnlocksSentencesItems;
  const sessionType = mode === 'cram' ? 'cram_sentences' : 'recent_unlocks_sentences';
  const emptyMessage = mode === 'cram' ? 'No sentences in your recent mistakes.' : 'No recently unlocked sentences to practice.';
  const { userId, userData, userLoading, emailVerified, subscriptionStatus } = useAuth();
  const { learnedWordIds, wordIdsLoading } = useMetadata();
  const { showModal, hideModal, modalStack } = useModal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Session state
  const [cramSentences, setCramSentences] = useState([]);
  const [correctSentences, setCorrectSentences] = useState([]);
  const [wrongSentences, setWrongSentences] = useState([]);
  const [markedWrongIds, setMarkedWrongIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [checkSubmitted, setCheckSubmitted] = useState(false);
  const [inputResults, setInputResults] = useState([]);
  const [lastProcessedIndex, setLastProcessedIndex] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFetchKey, setAudioFetchKey] = useState(0);
  const [sendResult, setSendResult] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [userChoice, setUserChoice] = useState(null);
  const [sessionSettings, setSessionSettings] = useState(() => {
    return (
      JSON.parse(localStorage.getItem('sessionSettings')) || {
        playCorrectSound: true,
        playWrongSound: true,
        showTranslation: true,
        inputMode: 'mixed',
        simpleAnswerMode: false,
        showFlippingAnimation: true,
        reducedAnimations: false,
        showKaraokeMode: true,
        autoPlayAfterAnswer: false,
        stayOnQuestionUntilCorrect: false,
      }
    );
  });
  const [pendingRetry, setPendingRetry] = useState(false);

  const inputRef = useRef(null);
  const initialFetchDone = useRef(false);
  const levelUpSound = useMemo(() => new Audio(levelUpSoundFile), []);
  const currentSentence = cramSentences[currentSentenceIndex];
  const isFullyLoading = loading || wordIdsLoading;

  // Auth guard
  useEffect(() => {
    if (userLoading) return;
    if (userId && !emailVerified) {
      navigate('/dashboard', { state: { errorMessage: 'Please verify your email to start a session.' } });
    }
  }, [userLoading, userId, emailVerified, navigate]);

  // Error message from navigation state
  useEffect(() => {
    if (location.state?.errorMessage) {
      showToast(location.state.errorMessage, 'error');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showToast, navigate, location.pathname]);

  // Audio fetch
  useEffect(() => {
    let isMounted = true;
    const fetchAudio = async () => {
      if (currentSentence?.sound) {
        try {
          const url = await getSound(currentSentence.sound, 'sentences');
          if (isMounted) setAudioUrl(url);
        } catch (error) {
          console.error('Error fetching audio URL:', error);
        }
      } else {
        setAudioUrl(null);
      }
    };
    fetchAudio();
    return () => { isMounted = false; };
  }, [currentSentence, audioFetchKey]);

  // Focus input
  useEffect(() => {
    if (!isFullyLoading && currentSentence && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFullyLoading, currentSentence, checkSubmitted]);

  // Fetch cram sentences
  useEffect(() => {
    const fetchItems = async () => {
      if (!userId || !userData || initialFetchDone.current) return;
      // Claim the fetch before awaiting. `userData` is a live snapshot object,
      // so any user-doc write re-runs this effect and could otherwise slip past
      // the guard and start a second full session load.
      initialFetchDone.current = true;
      setLoading(true);
      try {
        const items = await fetchFn(userId);
        if (items.length === 0) {
          navigate('/dashboard', { state: { errorMessage: emptyMessage } });
          return;
        }
        setCramSentences(items);
        setTotalCount(items.length);
      } catch (error) {
        initialFetchDone.current = false; // release the claim so a later snapshot can retry
        console.error('Error fetching cram sentences:', error);
        showToast('Failed to load cram session.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && userId && userData) {
      fetchItems();
    }
  }, [userId, userData, navigate, userLoading, showToast, fetchFn, emptyMessage]);

  // Prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.returnValue = 'Items you did not clear will remain in your cram list.';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => {
      if (e.touches?.[0]?.touchType === "stylus") return;
      let el = e.target;
      while (el && el !== document.body) {
        const { overflowY, overflowX } = window.getComputedStyle(el);
        if (
          ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) ||
          ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth)
        ) return;
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', preventTouchMove);
  }, []);

  const handlePlayEnd = useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleWordClick = useCallback(
    (word) => showModal(<DetailsModal item={{ ...word }} handleClose={hideModal} />),
    [showModal, hideModal]
  );

  const handleReportClick = useCallback(
    (currentUserInput) => showModal(<ReportIssueModal item={currentSentence} onClose={hideModal} userInput={currentUserInput} />),
    [showModal, hideModal, currentSentence]
  );

  const handleCulturalContextClick = useCallback(() => {
    if (currentSentence) showModal(<DetailsModal item={currentSentence} handleClose={hideModal} initialTab="context" />);
  }, [showModal, hideModal, currentSentence]);

  const handlePolitenessClick = useCallback(() => {
    if (currentSentence) showModal(<DetailsModal item={currentSentence} handleClose={hideModal} initialTab="politeness" />);
  }, [showModal, hideModal, currentSentence]);

  const handleCorrectReview = useCallback(() => {
    if (!currentSentence) return;
    setUserChoice(true);
    setLastProcessedIndex(currentSentenceIndex);
    setCorrectCount((prev) => prev + 1);
    setSendResult('correct');
    if (sessionSettings?.playCorrectSound) levelUpSound.play();
  }, [currentSentence, currentSentenceIndex, sessionSettings, levelUpSound]);

  const handleWrongReview = useCallback(() => {
    if (currentSentence) setMarkedWrongIds((prev) => new Set(prev).add(currentSentence.id));
    setUserChoice(false);
    setLastProcessedIndex(currentSentenceIndex);
    setSendResult('wrong');
    setPendingRetry(true);
  }, [currentSentence, currentSentenceIndex]);

  const handleCheck = useCallback(() => {
    if (!currentSentence || !userInput) return;
    const { inputResults: newInputResults, isCorrect: isInputCorrect } = checkSentenceAnswer(
      currentSentence,
      userInput,
      sessionSettings?.inputMode || 'mixed'
    );

    setInputResults(newInputResults);
    setCheckSubmitted(true);
    setIsCorrect(isInputCorrect);

    if (isInputCorrect) {
      handleCorrectReview();
    } else {
      handleWrongReview();
    }
  }, [currentSentence, userInput, sessionSettings, handleCorrectReview, handleWrongReview]);

  const navigateToCramEnd = useCallback(
    (finalCorrect, finalWrong) => {
      const sanitize = (s) => {
        const sanitized = { ...s, learned: true };
        delete sanitized.sentence_ref;
        if (sanitized.words) {
          sanitized.words = sanitized.words.map((w) => {
            const cleanWord = { ...w };
            delete cleanWord.word_id;
            delete cleanWord.word_ref;
            return cleanWord;
          });
        }
        return sanitized;
      };
      navigate('/session/end', {
        state: {
          correctSentences: finalCorrect.map(sanitize),
          wrongSentences: finalWrong.map(sanitize),
          type: sessionType,
        },
      });
    },
    [navigate, sessionType]
  );

  const handleNextSentence = useCallback(() => {
    const shouldStay = pendingRetry && sessionSettings?.stayOnQuestionUntilCorrect;
    setPendingRetry(false);
    if (shouldStay) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
      setUserChoice(null);
      setLastProcessedIndex(null);
      setSendResult(null);
      setAudioUrl(null);
      setAudioFetchKey((prev) => prev + 1);
      inputRef.current?.focus();
      return;
    }
    setUserChoice(null);
    setAudioUrl(null);
    setAudioFetchKey((prev) => prev + 1);

    if (lastProcessedIndex !== null) {
      let newCorrect = correctSentences;
      let newWrong = wrongSentences;

      if (sendResult === 'correct' && currentSentence) {
        // Split based on whether item was ever wrong this session
        if (markedWrongIds.has(currentSentence.id)) {
          newWrong = [...wrongSentences, currentSentence];
          setWrongSentences(newWrong);
        } else {
          newCorrect = [...correctSentences, currentSentence];
          setCorrectSentences(newCorrect);
        }

        setCramSentences((prev) => {
          const updated = prev.filter((_, i) => i !== lastProcessedIndex);
          if (updated.length === 0) {
            navigateToCramEnd(newCorrect, newWrong);
            return [];
          }
          setCurrentSentenceIndex(Math.floor(Math.random() * updated.length));
          return updated;
        });
      } else {
        // Wrong — keep item in queue, pick a different one if possible
        setCramSentences((prev) => {
          if (prev.length > 1) {
            let nextIndex;
            do {
              nextIndex = Math.floor(Math.random() * prev.length);
            } while (nextIndex === lastProcessedIndex);
            setCurrentSentenceIndex(nextIndex);
          }
          return prev;
        });
      }

      setLastProcessedIndex(null);
      setSendResult(null);
    } else {
      // Not yet answered — pick random different item
      if (cramSentences.length > 1) {
        let nextIndex;
        do {
          nextIndex = Math.floor(Math.random() * cramSentences.length);
        } while (nextIndex === currentSentenceIndex);
        setCurrentSentenceIndex(nextIndex);
      }
    }

    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIsCorrect(null);
    inputRef.current?.focus();
  }, [
    lastProcessedIndex,
    sendResult,
    currentSentence,
    correctSentences,
    wrongSentences,
    markedWrongIds,
    cramSentences,
    currentSentenceIndex,
    navigateToCramEnd,
    pendingRetry,
    sessionSettings?.stayOnQuestionUntilCorrect,
  ]);

  const handleSkipConfirm = useCallback(() => {
    if (!currentSentence) return;
    skipSentence(userId, currentSentence.id, subscriptionStatus.isSubscribed)
      .then((result) => { if (result?.status === 'already_skipped') showToast('This sentence is already skipped.', 'info'); })
      .catch(console.error);
    if (sessionSettings?.playCorrectSound) levelUpSound.play();
    setAudioUrl(null);

    const newCorrect = [...correctSentences, { ...currentSentence, srs_stage: 7 }];
    setCorrectSentences(newCorrect);
    setCramSentences((prev) => {
      const updated = prev.filter((_, i) => i !== currentSentenceIndex);
      if (updated.length === 0) {
        navigateToCramEnd(newCorrect, wrongSentences);
        return [];
      }
      setCurrentSentenceIndex(Math.floor(Math.random() * updated.length));
      return updated;
    });
    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIsCorrect(null);
    setUserChoice(null);
    inputRef.current?.focus();
  }, [currentSentence, userId, subscriptionStatus.isSubscribed, sessionSettings, levelUpSound, correctSentences, wrongSentences, currentSentenceIndex, navigateToCramEnd, showToast]);

  const handleSkipClick = useCallback(() => {
    showModal(
      <ConfirmationModal
        type="skip"
        onClose={hideModal}
        onConfirm={() => { handleSkipConfirm(); hideModal(); }}
      />
    );
  }, [showModal, hideModal, handleSkipConfirm]);

  const handleSettingsUpdate = (newSettings) => {
    if (newSettings.simpleAnswerMode !== sessionSettings.simpleAnswerMode && userChoice === null) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
    }
    setSessionSettings(newSettings);
  };

  const handleSimpleModeChoice = (isCorrectChoice) => {
    if (userChoice !== null) return;
    setUserChoice(isCorrectChoice);
    setIsCorrect(isCorrectChoice);
    setInputResults(currentSentence.words.map((w) => ({ correct: isCorrectChoice, text: w.sentence_form })));
    if (isCorrectChoice) {
      handleCorrectReview();
    } else {
      handleWrongReview();
    }
    setCheckSubmitted(true);
  };

  const isAnyModalOpen = modalStack.length > 0;


  const [isPenMode, setIsPenMode] = useState(false);
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (e.pointerType === 'pen') {
        setIsPenMode(true);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  if ((isFullyLoading && cramSentences.length === 0) || !currentSentence)
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Spinner />
      </div>
    );

  return (
    <div className="h-dvh overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 z-40">
          <SessionLayout
            updateSettings={handleSettingsUpdate}
            correctCount={correctCount}
            totalCount={totalCount}
          />
        </div>

        {/* Card area */}
        <div className={`${!sessionSettings.simpleAnswerMode && isPenMode ? 'flex-shrink flex-grow-0' : 'flex-1'} min-h-0 flex flex-col w-full max-w-4xl mx-auto`}>
          <AudioPlayerProvider audioSrc={audioUrl} onPlayEnd={handlePlayEnd} isAutoplay={!checkSubmitted} keepAwake={sessionSettings.keepAudioDeviceAwake}>
            <InteractiveCard
              currentItem={currentSentence}
              isSubmitted={checkSubmitted}
              showFlippingAnimation={sessionSettings.showFlippingAnimation}
              isIgnored={false}
              isGraded={userChoice !== null}
              showTranslation={checkSubmitted && sessionSettings.showTranslation}
              answerResult={sendResult}
              onItemClick={handleWordClick}
              isHotkeysEnabled={!isAnyModalOpen}
              onIgnore={null}
              onSkip={handleSkipClick}
              onReport={handleReportClick}
              onCulturalContext={handleCulturalContextClick}
              onPoliteness={handlePolitenessClick}
              levelUpAlertState={{ show: false, level: '', type: '', prevLevel: undefined }}
              learnedWordIds={learnedWordIds}
              userInput={userInput}
              sessionSettings={sessionSettings}
              shrinkToContent={!sessionSettings.simpleAnswerMode && isPenMode}
            />
          </AudioPlayerProvider>
        </div>

        {/* Input section */}
        <div className="flex-shrink-0 w-full bg-background-secondary border-t border-border z-50">
          <div className="w-full max-w-4xl mx-auto p-4 session-input-area">
            {sessionSettings.simpleAnswerMode ? (
              <SimpleAnswerSection
                showResultCard={checkSubmitted}
                onReveal={() => setCheckSubmitted(true)}
                onChoice={handleSimpleModeChoice}
                userChoice={userChoice}
                isIgnored={false}
                onNext={handleNextSentence}
              />
            ) : (
              <InputSection
                ref={inputRef}
                checkSubmitted={checkSubmitted}
                userInput={userInput}
                inputResults={inputResults}
                setUserInput={setUserInput}
                handleCheck={handleCheck}
                handleNext={handleNextSentence}
                isCorrect={isCorrect}
                isIgnored={false}
                isAnyModalOpen={isAnyModalOpen}
                inputMode={sessionSettings?.inputMode || 'mixed'}
                autoConvertToKana={sessionSettings?.autoConvertToKana ?? true}
                reducedAnimations={sessionSettings?.reducedAnimations}
              />
            )}
          </div>
        </div>

        {/* Palm rest spacer: only in pen mode */}
        {!sessionSettings.simpleAnswerMode && isPenMode && <div className="flex-1 min-h-0 pointer-events-none" />}
      </div>
    </div>
  );
};

export default CramSentencesSessionPage;